/**
 * The set of work tasks, when Rio opens several (preview.workspace.multi):
 * what choosing a kind does to the minutes, and Rio's word when a set doesn't
 * fit them.
 *
 * Each task takes its own minutes and the work space's minutes are the whole
 * budget, so adding a kind raises the work space to what the set needs, at once
 * and with no request. Other sections may give up minutes to make room, so the
 * card says which, with an Undo; taking the kind off again puts the minutes
 * back when nobody has touched them since. Rio decides how many a section can
 * hold, and says so in its refusal ("a section can have at most N"): the page
 * assumes today's limit until Rio says otherwise, and asks Rio before refusing
 * anything itself, so a raised limit needs no change here. A set that doesn't
 * fit is not applied; Rio's message is shown with the chips instead. The same
 * check runs on the set the card holds (debounced), so a refusal caused any
 * other way (a shorter interview shrinking the work space, a saved set) is
 * said too, with a way to give the work space the minutes it needs.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  InterviewerConfig,
  InterviewerPreview,
  Posting,
  SectionKey,
  WorkspaceKind,
} from '../../services/interviewer.types';
import { useInterviewerService } from '../../interviewer/serviceContext';
import {
  MAX_SECTION,
  MAX_TOTAL,
  describeMinuteChanges,
  giveWorkspaceMinutes,
  kindsOf,
  minutesOf,
  minutesToGiveFrom,
  moveWorkspaceKind,
  sameMinutes,
  sectionLimitFrom,
  setMinutes,
  setWorkspace,
  toggleWorkspaceKind,
  withMinutes,
  type MinutesSnapshot,
} from '../../interviewer/model';

export const WORK_CHECK_MS = 400;

const SET_PATH = 'workspace.kinds';

/** Said when a pick's answer from Rio arrives after the card has moved on. */
export const COULDNT_ADD = "Couldn't add that. Try again.";

/** Minutes the card changed to fit the work tasks, kept so it can say so and put them back. */
interface Adjustment {
  /** The kind whose adding made it, or null for minutes given on request. */
  kind: WorkspaceKind | null;
  /** The kinds before, for Undo. */
  kinds: WorkspaceKind[];
  before: MinutesSnapshot;
  after: MinutesSnapshot;
  note: string;
}

interface Options {
  config: InterviewerConfig;
  preview: InterviewerPreview;
  posting: Posting;
  onChange: (change: (current: InterviewerConfig) => InterviewerConfig) => void;
  /** Several tasks are offered (Rio says `multi`). Off, nothing is requested. */
  active: boolean;
}

const workspaceMinutes = (config: InterviewerConfig) =>
  config.sections.find((s) => s.key === 'workspace')?.minutes ?? 0;

const joined = (parts: string[]) =>
  parts.length < 2
    ? (parts[0] ?? '')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

const fitPhrase = (count: number) =>
  count === 2
    ? 'the second task'
    : count === 3
      ? 'the third task'
      : 'the work tasks';

export const useWorkTasks = ({
  config,
  preview,
  posting,
  onChange,
  active,
}: Options) => {
  const service = useInterviewerService();
  // Rio's refusal of a kind the recruiter tried to add, which wasn't applied.
  const [refused, setRefused] = useState<string | null>(null);
  // Rio's refusal of the set the card holds.
  const [checked, setChecked] = useState<string | null>(null);
  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);
  const limit = useRef(MAX_SECTION);
  // One per check of the set, and one per pick: a pick is only answered for
  // while it is the latest, and says so when the card changed under it.
  const checkTicket = useRef(0);
  const pickTicket = useRef(0);
  const configRef = useRef(config);
  configRef.current = config;
  const previewRef = useRef(preview);
  previewRef.current = preview;
  const postingRef = useRef(posting);
  postingRef.current = posting;
  const adjustmentsRef = useRef(adjustments);
  adjustmentsRef.current = adjustments;

  const learn = (message: string) => {
    const said = sectionLimitFrom(message);
    if (said) limit.current = said;
  };
  const titleOf = (key: SectionKey) =>
    previewRef.current.sections.find((s) => s.key === key)?.title ?? key;

  const kinds = kindsOf(config);
  const kindsKey = kinds.join();
  const { enabled } = config.workspace;
  const minutes = workspaceMinutes(config);
  const several = active && enabled && kinds.length > 1;

  // A refusal belongs to the set and minutes it was about.
  useEffect(() => {
    setRefused(null);
  }, [kindsKey, minutes]);

  // What the card changed is only worth saying, or putting back, while the
  // minutes are as it left them.
  const latest = adjustments[adjustments.length - 1];
  const live =
    latest && sameMinutes(minutesOf(config), latest.after) ? latest : null;
  useEffect(() => {
    if (adjustments.length && !live) setAdjustments([]);
  }, [adjustments.length, live]);

  // Rio's word on the set as it stands.
  useEffect(() => {
    const mine = ++checkTicket.current;
    setChecked(null);
    if (!several) return;
    const timer = setTimeout(async () => {
      try {
        const result = await service.validate(
          configRef.current,
          postingRef.current,
        );
        if (mine !== checkTicket.current) return;
        const hit = result.problems.find((p) => p.path === SET_PATH);
        if (hit) learn(hit.message);
        setChecked(hit?.message ?? null);
      } catch {
        // A failed check says nothing; Rio checks again on save.
      }
    }, WORK_CHECK_MS);
    return () => clearTimeout(timer);
  }, [several, kindsKey, minutes, service]);

  /** The set with the work space raised to what it needs, when it needs it and the interview can hold it. */
  const fitted = useCallback(
    (next: InterviewerConfig): InterviewerConfig | null => {
      if (kindsOf(next).length < 2) return next;
      const need = setMinutes(kindsOf(next), previewRef.current.workspace);
      return giveWorkspaceMinutes(next, need);
    },
    [],
  );

  const tooLong = (need: number) =>
    `The interview runs at most ${MAX_TOTAL} minutes, so it can't hold ${need} minutes of work tasks. Choose fewer tasks.`;

  /** Applies a change that moved minutes, and remembers what moved. */
  const commit = useCallback(
    (
      current: InterviewerConfig,
      next: InterviewerConfig,
      kind: WorkspaceKind | null,
      fits = fitPhrase(kindsOf(next).length),
    ) => {
      const before = minutesOf(current);
      const after = minutesOf(next);
      if (!sameMinutes(before, after)) {
        const changes = describeMinuteChanges(before, after, titleOf);
        const entry: Adjustment = {
          kind,
          kinds: kindsOf(current),
          before,
          after,
          note: `${joined(changes)} to fit ${fits}.`,
        };
        setAdjustments((list) => [...list, entry]);
      }
      onChange(() => next);
    },
    [onChange],
  );

  const pick = useCallback(
    (kind: WorkspaceKind) => {
      setRefused(null);
      const mine = ++pickTicket.current;
      const current = configRef.current;
      const next = toggleWorkspaceKind(current, kind);
      if (next === current) return;
      const chosen = kindsOf(next);
      const taking = kind !== 'auto' && kindsOf(current).includes(kind);
      if (taking) {
        // Off again: the minutes the kind took go back, when untouched since.
        const latestNow = adjustmentsRef.current.slice(-1)[0];
        if (
          latestNow?.kind === kind &&
          sameMinutes(minutesOf(current), latestNow.after)
        ) {
          setAdjustments((list) => list.slice(0, -1));
          onChange(() => withMinutes(next, latestNow.before));
          return;
        }
      }
      if (chosen.length < 2) {
        if (!taking) setAdjustments([]);
        onChange((c) => toggleWorkspaceKind(c, kind));
        return;
      }
      const need = setMinutes(chosen, previewRef.current.workspace);
      const apply = () => {
        const now = configRef.current;
        const toggled = toggleWorkspaceKind(now, kind);
        commit(now, fitted(toggled) ?? toggled, kind);
      };
      if (need <= limit.current) {
        if (fitted(next)) apply();
        else setRefused(tooLong(need));
        return;
      }
      // More than the limit the page knows: Rio says whether that is still
      // the limit, in its own words.
      const startedWith = kindsOf(current).join();
      // Rio is asked about the work space's own minutes, as they would be.
      const candidate: InterviewerConfig = fitted(next) ?? {
        ...next,
        sections: next.sections.map((s) =>
          s.key === 'workspace' ? { ...s, minutes: need } : s,
        ),
      };
      const answer = (change: () => void) => {
        // A newer pick answers for itself; anything else that changed the set
        // meanwhile makes this answer about a set that is gone.
        if (mine !== pickTicket.current) return;
        if (kindsOf(configRef.current).join() !== startedWith) {
          setRefused(COULDNT_ADD);
          return;
        }
        change();
      };
      service
        .validate(candidate, postingRef.current)
        .then((result) =>
          answer(() => {
            const hit = result.problems.find((p) => p.path === SET_PATH);
            if (hit) {
              learn(hit.message);
              setRefused(hit.message);
            } else {
              limit.current = Math.max(limit.current, need);
              if (fitted(next)) apply();
              else setRefused(tooLong(need));
            }
          }),
        )
        .catch(() =>
          answer(() =>
            setRefused(
              `These ${chosen.length} work tasks need ${need} minutes, but a section can have at most ${limit.current}. Choose fewer tasks.`,
            ),
          ),
        );
    },
    [commit, fitted, onChange, service],
  );

  const move = useCallback(
    (kind: WorkspaceKind, delta: -1 | 1) => {
      onChange((c) => moveWorkspaceKind(c, kind, delta));
    },
    [onChange],
  );

  /** The work task switched on or off; on, with several tasks, its minutes raised to fit. */
  const toggleTask = useCallback(() => {
    setRefused(null);
    onChange((c) => {
      const next = setWorkspace(c, { enabled: !c.workspace.enabled });
      if (!active || !next.workspace.enabled) return next;
      const need = setMinutes(kindsOf(next), previewRef.current.workspace);
      if (kindsOf(next).length < 2 || need > limit.current) return next;
      return giveWorkspaceMinutes(next, need) ?? next;
    });
  }, [active, onChange]);

  /** Rio's "Give it 30 minutes": the work space given them, on request. */
  const needed = checked && !refused ? minutesToGiveFrom(checked) : null;
  const give = useCallback(() => {
    if (!needed) return;
    const current = configRef.current;
    const next = giveWorkspaceMinutes(current, needed);
    if (!next) setRefused(tooLong(needed));
    else commit(current, next, null, 'the work tasks');
  }, [commit, needed]);

  /** Puts back the minutes the card changed, and the kind that made it change them. */
  const undo = useCallback(() => {
    const entry = adjustmentsRef.current.slice(-1)[0];
    if (!entry) return;
    setAdjustments((list) => list.slice(0, -1));
    setRefused(null);
    onChange((c) =>
      sameMinutes(minutesOf(c), entry.after)
        ? setWorkspace(withMinutes(c, entry.before), { kinds: entry.kinds })
        : c,
    );
  }, [onChange]);

  return {
    problem: refused ?? checked,
    /** A fix Rio's refusal allows, when it has one. */
    fix: needed ? { label: `Give it ${needed} minutes`, run: give } : null,
    /** What the card changed to fit the tasks just added, while it stands. */
    adjusted: live ? { note: live.note, undo } : null,
    pick,
    move,
    toggleTask,
  };
};
